import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useDialogs } from "../../../../dialog/use-dialog";
import { useNotification } from "../../../../../context/notification-provider";
import { DistinctValue } from "../../../types";
import { purgeRows } from "../../../services/cleaning-data-service";

interface PurgeEntitiesFooterProps {
  fieldId: number | undefined;
  checked: { [key: string]: boolean };
  mainValues: DistinctValue[];
  selectedItem: DistinctValue | null;
  refreshDistinctValues: () => void;
}

/**
 * Footer for Purge Entities category (categoryId: 8)
 */
const PurgeEntitiesFooter: React.FC<PurgeEntitiesFooterProps> = ({
  fieldId,
  checked,
  mainValues,
  selectedItem,
  refreshDistinctValues,
}) => {
  const { confirm } = useDialogs();
  const { showNotification } = useNotification();

  const handlePurge = async () => {
    if (fieldId === undefined) {
      showNotification({
        message: "No field selected.",
        severity: "error",
        duration: 3000,
      });
      return;
    }

    // Get values to purge: prioritize checked items, then fall back to selected item, then all entities
    let valuesToPurge: string[] = [];
    const checkedValues = mainValues
      .filter((val) => checked[val.id])
      .map((val) => val.value);

    if (checkedValues.length > 0) {
      valuesToPurge = checkedValues;
    } else if (selectedItem) {
      valuesToPurge = [selectedItem.value];
    } else {
      // If nothing selected, purge all entities in the list
      valuesToPurge = mainValues.map((val) => val.value);
    }

    if (valuesToPurge.length === 0) {
      showNotification({
        message: "No entities available to purge.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      const result = await confirm(
        `Are you sure you want to permanently purge ${valuesToPurge.length} entit${valuesToPurge.length > 1 ? "ies" : "y"}? This will remove entire rows from the dataset. This action cannot be undone.`,
        {
          title: "Confirm Purge Entities",
          okText: "Purge Entities",
          cancelText: "Cancel",
          severity: "error",
        },
      );

      if (result === true) {
        try {
          await purgeRows(fieldId, valuesToPurge);
          refreshDistinctValues();
          showNotification({
            message: "Entities purged successfully.",
            severity: "success",
            duration: 3000,
          });
        } catch (err) {
          showNotification({
            message: `Failed to purge entities. Reason: ${(err as Error).message}`,
            severity: "error",
            duration: 3000,
          });
        }
      }
    } catch {
      showNotification({
        message: "Failed to show confirmation dialog.",
        severity: "error",
        duration: 3000,
      });
    }
  };

  const handleCancel = () => {
    // No action needed for cancel in purge
  };

  // Determine the message to display
  const checkedCount = Object.values(checked).filter(Boolean).length;
  const totalCount = mainValues.length;
  let message = `Purge all ${totalCount} entit${totalCount === 1 ? "y" : "ies"} (entire rows) from the dataset.`;
  if (checkedCount > 0) {
    message = `Purge ${checkedCount} entit${checkedCount === 1 ? "y" : "ies"} (entire rows) from the dataset.`;
  } else if (selectedItem) {
    message = `Purge entity (entire row) with "${selectedItem.value}" from the dataset.`;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid #eee",
          p: 2,
        }}
      >
        <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
          Warning: This will permanently remove entire entities (rows) from the
          dataset.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {message}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          borderTop: "1px solid #eee",
          p: 2,
        }}
      >
        <Button
          variant="contained"
          color="error"
          size="small"
          sx={{ mr: 1, borderRadius: 1 }}
          onClick={handlePurge}
        >
          Purge Entities
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={{ borderRadius: 1 }}
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default PurgeEntitiesFooter;
